import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faCaretUp,
  faListUl
} from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import { DeviceUnitRecord } from 'core/deviceManagement/DeviceManagement';
import {
  DEVICE_IMPORT_ISSUE_I18N,
  DeviceImportIssue,
  DeviceImportList,
  findDeviceImportMismatches,
  findDeviceImportMisses
} from './deviceImportList';
import styles from './deviceManagement.module.scss';

type DeviceListModeBandProps = {
  list: DeviceImportList;
  /** 目前條件下的總筆數，也就是名單命中幾筆。 */
  hitCount: number;
  /**
   * 整份名單比對過之後真的存在於這個渠道的那幾列。比對還沒回來、或名單大到
   * 一次問不完時是 undefined，那時一筆需確認都不報。
   *
   * 不可以換成清單當下那一頁：需確認數是對整份名單下的結論，用一頁去算的話，
   * 同一份名單在第 1 頁與第 2 頁會得到兩個不同的數字。
   */
  matchedUnits?: DeviceUnitRecord[];
  onExit: () => void;
};

/**
 * 名單模式的橫幅。
 *
 * 「共」數的是輸入了幾筆，前端自己知道；「命中」是查詢回應的總筆數，由後端算 ——
 * 兩個數字來自不同的地方，所以不要拿其中一個去推另一個。名單條件與其他篩選條件
 * 是疊加的，所以命中數會隨著其他條件一起變小，那是對的。
 */
export const DeviceListModeBand: React.FC<DeviceListModeBandProps> = ({
  list,
  hitCount,
  matchedUnits,
  onExit
}) => {
  const [open, setOpen] = useState(false);

  // 店號由同一批列推出來，不另外傳一份進來：兩份各自傳的話，有一天會對不起來，
  // 而對不起來的症狀是同一筆同時被報成「查無此店號」與「縣市對不上」。
  const matchedStoreNums = useMemo(
    () => matchedUnits?.map((record) => record.storeNum),
    [matchedUnits]
  );

  // 三種都算進「需確認」：解不出店號的、店號查不到的、以及查得到但縣市/店名對不上的。
  // 查不到的那一種與另外兩種互斥 —— 對不上的前提是查得到。
  const issues = useMemo(() => {
    const unparsed = list.entries.filter((entry) => entry.issue !== undefined);
    const misses = findDeviceImportMisses(list.entries, matchedStoreNums).map(
      (entry) => ({ ...entry, issue: DeviceImportIssue.NOT_FOUND })
    );
    const mismatches = findDeviceImportMismatches(
      list.entries,
      matchedUnits ?? []
    ).map((entry) => ({ ...entry, issue: DeviceImportIssue.MISMATCH }));
    return [...unparsed, ...misses, ...mismatches];
  }, [list.entries, matchedUnits, matchedStoreNums]);

  return (
    <div className={styles.listModeBand} data-testid='device-list-mode-band'>
      <div className={styles.listModeSummary}>
        <span className={styles.listModeBadge}>
          <FontAwesomeIcon icon={faListUl} />
          {i18n.t('deviceManagement.labels.listMode')}
        </span>
        <span className={styles.listModeFileName}>{list.fileName}</span>
        <span className={styles.listModeCount} data-testid='list-mode-count'>
          {i18n.t('deviceManagement.labels.listModeCount', {
            total: list.entries.length,
            hit: hitCount
          })}
        </span>
        {issues.length > 0 && (
          <span
            className={styles.listModeIssueToggle}
            onClick={() => setOpen((prev) => !prev)}
            data-testid='list-mode-issue-toggle'
          >
            {i18n.t('deviceManagement.labels.listModeIssueCount', {
              total: issues.length
            })}
            <FontAwesomeIcon icon={open ? faCaretUp : faCaretDown} />
          </span>
        )}
        <span
          className={styles.listModeExit}
          onClick={onExit}
          data-testid='list-mode-exit'
        >
          {i18n.t('deviceManagement.buttons.exitListMode')}
        </span>
      </div>
      {open && (
        <div className={styles.listModeIssues} data-testid='list-mode-issues'>
          {issues.map((entry, index) => (
            <div
              className={styles.listModeIssueRow}
              key={`${entry.raw}-${index}`}
            >
              <span className={styles.listModeIssueRaw}>{entry.raw}</span>
              <span>
                {i18n.t(
                  DEVICE_IMPORT_ISSUE_I18N[entry.issue as DeviceImportIssue]
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
